import { useEffect, useRef, type MutableRefObject } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { SpinnerIcon } from "@/lib/icons";
import { Skeleton } from "@/components/ui/skeleton";
import { useColumnTasks } from "@/hooks/useColumnTasks";
import type { TaskFilterParams } from "@/hooks/useTasks";
import type { TaskExtended } from "@/types";
import type { TaskCompletionToggle } from "@/hooks/useTaskCompletionToggle";
import { KanbanTaskCard } from "./KanbanTaskCard";
import type { KanbanColumnDef } from "@/lib/taskBoardMeta";

// Mobile single-column task list. Mirrors KanbanColumn's pagination &
// virtualization behaviour but drops the column header (the tab above
// already labels the status) and is meant to fill the available height
// inside its parent flex container.
export function MobileTaskList({
  column,
  filterParams,
  projectNameById,
  plotNameById,
  onTaskClick,
  onToggleCompleted,
  refetchRef,
  onCount,
}: {
  column: KanbanColumnDef;
  filterParams: TaskFilterParams;
  projectNameById: Map<string, string>;
  plotNameById: Map<string, string>;
  onTaskClick: (task: TaskExtended) => void;
  onToggleCompleted?: TaskCompletionToggle;
  refetchRef?: MutableRefObject<(() => void) | null>;
  // Reports the column's totalCount up to the parent so tabs can show counts.
  onCount?: (count: number) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const skipQuery = column.statuses !== undefined && column.statuses.length === 0;
  const result = useColumnTasks(column.status, filterParams, skipQuery, column.statuses);
  const tasks = skipQuery ? [] : result.tasks;
  const totalCount = skipQuery ? 0 : result.totalCount;
  const hasNextPage = skipQuery ? false : result.hasNextPage;
  const loading = skipQuery ? false : result.loading;
  const isFetchingMore = skipQuery ? false : result.isFetchingMore;
  const { fetchNextPage, refetch } = result;

  // Skeletons on the first load only — see KanbanColumn for why a refetch
  // must not blank the list.
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (!loading && !skipQuery) hasLoadedRef.current = true;
  }, [loading, skipQuery]);
  const showSkeletons = loading && !hasLoadedRef.current;

  useEffect(() => {
    if (refetchRef) {
      refetchRef.current = refetch;
    }
  }, [refetchRef, refetch]);

  useEffect(() => {
    onCount?.(totalCount);
  }, [totalCount, onCount]);

  const virtualizer = useVirtualizer({
    count: tasks.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 180,
    overscan: 8,
    getItemKey: (index) => tasks[index]?.id ?? index,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const lastItem = virtualItems.at(-1);
    if (!lastItem) return;
    if (lastItem.index >= tasks.length - 8 && hasNextPage && !isFetchingMore) {
      fetchNextPage();
    }
  }, [virtualItems, tasks.length, hasNextPage, isFetchingMore, fetchNextPage]);

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0 px-3 pt-3">
      {showSkeletons ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          Ei tehtäviä
        </div>
      ) : (
        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {virtualItems.map((virtualItem) => (
            <KanbanTaskCard
              key={tasks[virtualItem.index].id}
              task={tasks[virtualItem.index]}
              projectNameById={projectNameById}
              plotNameById={plotNameById}
              onClick={() => onTaskClick(tasks[virtualItem.index])}
              onToggleCompleted={onToggleCompleted}
              measureRef={virtualizer.measureElement}
              virtualItem={virtualItem}
            />
          ))}
        </div>
      )}
      {isFetchingMore && (
        <div className="flex justify-center py-3">
          <SpinnerIcon className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}
